/**
 * Ключи задач (DEV-1421, SALES-18) в тексте становятся ссылками на карточку.
 *
 * Сделано декорациями, а не разметкой в документе: ключ так и остаётся в
 * markdown голым текстом, каким его и напечатали. Иначе каждое упоминание
 * задачи в описании превращалось бы в ссылку внутри хранилища, а при
 * переименовании проекта — в битую ссылку.
 *
 * Из этого же следует, что ссылки живут и в режиме чтения: декорации не
 * зависят от того, редактируется документ или нет.
 */
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

/**
 * Ключ вида DEV-1421. Хвост проверяется здесь, начало — в коде: `\b` между
 * цифрой и дефисом есть, поэтому в `ADR-0046-draft` он нашёл бы ключ, а
 * lookbehind ради этого тянуть не хочется — его до сих пор нет в части
 * мобильных браузеров.
 */
const ISSUE_KEY = /([A-Z][A-Z0-9]{1,9})-(\d{1,6})(?![\w-])/g

/** Слева от ключа не должно быть ни буквы, ни цифры, ни дефиса. */
const startsKey = (text: string, index: number): boolean =>
  index === 0 || !/[\w-]/.test(text[index - 1] ?? '')

export const ISSUE_KEY_CLASS = 'tracker-issue-key'

export interface IssueKeyLinkOptions {
  /** Куда вести по ключу. По умолчанию — страница задачи трекера */
  href: (key: string) => string
  /**
   * Переход внутри приложения. Без него ссылка открывается обычной
   * навигацией, то есть перезагрузкой — приложение подставляет сюда роутер.
   */
  open?: (key: string) => void
}

const decorate = (doc: ProseMirrorNode): DecorationSet => {
  const decorations: Decoration[] = []

  doc.descendants((node, position) => {
    if (!node.isText || !node.text) return

    // Внутри кода ключ — это текст примера, а не ссылка.
    if (node.marks.some((mark) => mark.type.name === 'code')) return

    for (const match of node.text.matchAll(ISSUE_KEY)) {
      const index = match.index ?? 0

      if (!startsKey(node.text, index)) continue

      const from = position + index

      decorations.push(
        Decoration.inline(from, from + match[0].length, {
          class: ISSUE_KEY_CLASS,
          'data-issue-key': match[0],
        }),
      )
    }
  })

  return DecorationSet.create(doc, decorations)
}

export const IssueKeyLink = Extension.create<IssueKeyLinkOptions>({
  name: 'issueKeyLink',

  addOptions() {
    return { href: (key: string) => `/issue/${key}` }
  },

  addProseMirrorPlugins() {
    const { href, open } = this.options

    return [
      new Plugin({
        key: new PluginKey('issueKeyLink'),
        state: {
          init: (_, state) => decorate(state.doc),
          apply: (transaction, previous) =>
            transaction.docChanged ? decorate(transaction.doc) : previous,
        },
        props: {
          decorations(state) {
            return this.getState(state)
          },
          handleClick(view, _position, event) {
            const target = (event.target as HTMLElement | null)?.closest(`.${ISSUE_KEY_CLASS}`)
            const key = target?.getAttribute('data-issue-key')

            if (!key) return false

            // Модификатор всегда открывает новую вкладку — так ведут себя все
            // ссылки, и ключ задачи не должен быть исключением.
            if (event.metaKey || event.ctrlKey) {
              window.open(href(key), '_blank', 'noopener')
            } else if (open) {
              open(key)
            } else {
              window.location.assign(href(key))
            }

            return true
          },
        },
      }),
    ]
  },
})
